// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — lookup helpers
// ═══════════════════════════════════════════════════════════════════════════
//
// Pure-function helpers for navigating an array of `CommandDescriptor`s.
// Every helper takes the descriptor array as a parameter — there is no
// implicit global registry — so consumers (tests, AI proxy, validators)
// can pass either the production registry or a mock array.
//
// Hot-path callers (CLI alias resolution, AI command lookup) should
// memoise via `buildLookupIndex` instead of re-scanning the array on
// every call.
// ═══════════════════════════════════════════════════════════════════════════

import type {
  CommandCategory,
  CommandDescriptor,
  CommandRegistryId,
} from "./types.js";

/**
 * Build a memoisable lookup index over a descriptor array. Returned
 * structure exposes O(1) lookup by id and by alias.
 *
 * Aliases are normalised to upper-case ASCII before insertion so callers
 * can pass any-case input to `byAlias`. RU aliases are stored verbatim
 * after upper-case (Cyrillic upper-case is well-defined).
 *
 * Duplicate aliases across descriptors result in the LAST descriptor
 * winning silently — the validator script catches this case as an error
 * (so production code can rely on cleaned-up data; the index is a
 * fast-path, not a parity check).
 */
export type CommandLookupIndex = {
  byId: ReadonlyMap<CommandRegistryId, CommandDescriptor>;
  byAlias: ReadonlyMap<string, CommandDescriptor>;
};

export function buildLookupIndex(
  descriptors: readonly CommandDescriptor[],
): CommandLookupIndex {
  const byId = new Map<CommandRegistryId, CommandDescriptor>();
  const byAlias = new Map<string, CommandDescriptor>();
  for (const d of descriptors) {
    byId.set(d.id, d);
    // Treat the id itself as a canonical alias.
    byAlias.set(d.id.toUpperCase(), d);
    for (const alias of d.aliases.en) byAlias.set(alias.toUpperCase(), d);
    for (const alias of d.aliases.ru) byAlias.set(alias.toUpperCase(), d);
  }
  return { byId, byAlias };
}

/**
 * Find a descriptor by its canonical `id`. Returns `null` when no match.
 */
export function getDescriptorById(
  id: CommandRegistryId,
  descriptors: readonly CommandDescriptor[],
): CommandDescriptor | null {
  for (const d of descriptors) {
    if (d.id === id) return d;
  }
  return null;
}

/**
 * Resolve a user-typed alias (case-insensitive, RU/EN) to the matching
 * descriptor. Falls back to id-match when the alias is the descriptor's
 * canonical id. Returns `null` when no match.
 *
 * NB: this scan is O(n × aliases); for hot paths build a `CommandLookupIndex`
 * once and reuse it.
 */
export function resolveAlias(
  alias: string,
  descriptors: readonly CommandDescriptor[],
): CommandDescriptor | null {
  const needle = alias.trim().toUpperCase();
  if (needle.length === 0) return null;
  for (const d of descriptors) {
    if (d.id.toUpperCase() === needle) return d;
    for (const a of d.aliases.en) {
      if (a.toUpperCase() === needle) return d;
    }
    for (const a of d.aliases.ru) {
      if (a.toUpperCase() === needle) return d;
    }
  }
  return null;
}

/**
 * List every descriptor in `category`, preserving the array's source order.
 * Filtering is O(n) so callers should cache the result if rendering many
 * categories at once.
 */
export function listByCategory(
  category: CommandCategory,
  descriptors: readonly CommandDescriptor[],
): CommandDescriptor[] {
  const out: CommandDescriptor[] = [];
  for (const d of descriptors) {
    if (d.category === category) out.push(d);
  }
  return out;
}

/**
 * Flat alias listing used by the validator script. Each entry records the
 * alias text, the language array it came from and the owning descriptor's
 * id — the validator then checks for cross-descriptor duplicates and
 * mismatched casing.
 *
 * The descriptor's canonical id is included with `lang: "en"` so
 * id-collisions are caught alongside alias-collisions.
 */
export function listAllAliases(
  descriptors: readonly CommandDescriptor[],
): Array<{
  alias: string;
  lang: "en" | "ru";
  descriptorId: CommandRegistryId;
}> {
  const out: Array<{
    alias: string;
    lang: "en" | "ru";
    descriptorId: CommandRegistryId;
  }> = [];
  for (const d of descriptors) {
    out.push({ alias: d.id, lang: "en", descriptorId: d.id });
    for (const a of d.aliases.en) {
      out.push({ alias: a, lang: "en", descriptorId: d.id });
    }
    for (const a of d.aliases.ru) {
      out.push({ alias: a, lang: "ru", descriptorId: d.id });
    }
  }
  return out;
}

/**
 * Convenience wrapper around `getDescriptorById` that throws when missing.
 * Use in tests / generator scripts where a missing id indicates a bug, not
 * a runtime user-input case.
 */
export function getDescriptorByIdOrThrow(
  id: CommandRegistryId,
  descriptors: readonly CommandDescriptor[],
): CommandDescriptor {
  const found = getDescriptorById(id, descriptors);
  if (found === null) {
    throw new Error(`command-registry: descriptor not found for id=${id}`);
  }
  return found;
}
